import React, { useContext, useEffect, useState } from 'react';
import { DateToString } from 'utils/stringUtils';
import ForecastSpread from 'components/ForecastSpread';
import WidgetTimeUpdate from './WidgetTimeUpdate';
import WidgetLoadingAnimation from './WidgetLoadingAnimation';
import { ErrorMessage } from './ErrorMessage';
import { useAPIData } from 'hooks/useAPIData';
import { WiDaySunnyOvercast } from 'react-icons/wi';
import { ColorContext, UniqueWidgetRefreshContext } from 'App';
import Cookies from 'js-cookie';
import { getDetailedMarine7 } from 'services/weather/marine.service';

const sortOrder = {
  Morning: 0,
  Afternoon: 1,
  Evening: 2,
  Night: 3,
};

let dataCache;

export default function SurfAlertsWidget({
  widgetRefresh,
  setWidgetRefresh,
  dimensions,
  containerSizePx,
}) {
  const dateTime = new Date();

  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState({ state: false, message: '' });
  const [data, setData] = useState();
  useEffect(() => {
    dataCache = null;
    setFetching(true);
  }, [widgetRefresh]);

  const theme = useContext(ColorContext);
  const FusionData = useAPIData({
    successCallback: () => {
      setFetching(false);
    },
    errorCallback: () => {
      setError({ state: true, message: 'Error fetching data' });
      setFetching(false);
    },
    fetchingCallback: () => {
      setFetching(true);
    },
  });

  const uwf = useContext(UniqueWidgetRefreshContext);
  const ROUTE = 'getDetailedMarine7';
  const LOCATION = Cookies.get('city') + '-' + Cookies.get('state');
  const UNIQUEKEY = LOCATION + '-' + ROUTE;
  const containerWidth = Number.isFinite(containerSizePx?.width)
    ? containerSizePx.width
    : dimensions?.[0] == 2
      ? 640
      : 320;
  const containerHeight = Number.isFinite(containerSizePx?.height)
    ? containerSizePx.height
    : dimensions?.[1] == 2
      ? 640
      : 320;
  const isWideWidget = dimensions?.[0] == 2 && dimensions?.[1] == 1;
  const isSquareWidget = dimensions?.[0] == 1 && dimensions?.[1] == 1;
  const isTallWidget = dimensions?.[0] == 1 && dimensions?.[1] == 2;
  const wideTight = isWideWidget && (containerWidth < 760 || containerHeight < 260);
  const wideMedium = isWideWidget && !wideTight && containerWidth < 920;
  const tallTight = isTallWidget && containerWidth < 380;
  const squareTight = isSquareWidget && containerWidth < 320;
  const widgetShellPadding = isSquareWidget ? (squareTight ? 8 : 9) : isTallWidget ? 9 : 10;
  const panelPadding = isSquareWidget ? (squareTight ? 8 : 10) : isTallWidget ? 9 : 12;
  const dayBlockMarginTop = isWideWidget
    ? wideTight
      ? 24
      : wideMedium
        ? 30
        : 34
    : isTallWidget
      ? tallTight
        ? 24
        : 28
      : squareTight
        ? 20
        : 24;
  const segmentMarginTop = isWideWidget ? (wideTight ? 10 : 12) : isTallWidget ? 10 : 8;
  const dayTitleFontSize = isWideWidget
    ? wideTight
      ? 18
      : wideMedium
        ? 20
        : 22
    : isTallWidget
      ? tallTight
        ? 16
        : 18
      : squareTight
        ? 14
        : 15;
  const spreadHeaderFontSize = isWideWidget
    ? wideTight
      ? 14
      : wideMedium
        ? 15
        : 16
    : isTallWidget
      ? tallTight
        ? 12
        : 13
      : squareTight
        ? 11
        : 12;
  const spreadBodyFontSize = isWideWidget
    ? wideTight
      ? 12
      : wideMedium
        ? 12.5
        : 13
    : isTallWidget
      ? tallTight
        ? 11
        : 11.5
      : squareTight
        ? 10
        : 10.5;
  const spreadHeaderClassName = 'fw-semibold w-100';
  const spreadDataItemClassName = 'd-flex align-items-start col';
  const spreadDataRowClassName =
    isSquareWidget || (isTallWidget && tallTight) ? 'row row-cols-1 g-0' : undefined;
  const spreadDataRowStyle = {
    rowGap: isWideWidget ? 2 : 1,
  };
  const spreadDataItemStyle = {
    fontSize: spreadBodyFontSize,
    lineHeight: 1.25,
    paddingTop: 2,
    paddingBottom: 2,
    minWidth: 0,
    overflowWrap: 'anywhere',
  };

  useEffect(() => {
    const fetchData = async () => {
      dataCache = null;
      try {
        FusionData.forceGet({
          key: ROUTE,
          apiFunction: getDetailedMarine7,
        }).then((res) => {
          let daySorted = {};

          res.data.result.forEach((item) => {
            let rawDate = new Date(item.iso8601);
            let adjDate = new Date(rawDate.setHours(rawDate.getHours() - 2)).setHours(0);
            let date = new Date(adjDate);

            if (
              Object.keys(daySorted)
                .map((key) => {
                  return key == date;
                })
                .includes(true)
            ) {
              daySorted[date] = {
                ...daySorted[date],
                [item.day_segment]: item,
              };
            } else {
              daySorted = {
                ...daySorted,
                [date]: {
                  [item.day_segment]: item,
                },
              };
            }
          });

          dataCache = { ...daySorted };
          setData(dataCache);
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [uwf[UNIQUEKEY]]);

  useEffect(() => {
    (async () => {
      if (dataCache) {
        setData(dataCache);
        setFetching(false);
        return;
      }
      FusionData.get({
        key: ROUTE,
        apiFunction: getDetailedMarine7,
      })
        .then((res) => {
          let daySorted = {};

          res.data.result.forEach((item) => {
            let rawDate = new Date(item.iso8601);
            let adjDate = new Date(rawDate.setHours(rawDate.getHours() - 2)).setHours(0);
            let date = new Date(adjDate);

            if (
              Object.keys(daySorted)
                .map((key) => {
                  return key == date;
                })
                .includes(true)
            ) {
              daySorted[date] = {
                ...daySorted[date],
                [item.day_segment]: item,
              };
            } else {
              daySorted = {
                ...daySorted,
                [date]: {
                  [item.day_segment]: item,
                },
              };
            }
          });

          dataCache = { ...daySorted };
          setData(dataCache);

          setFetching(false);
        })
        .catch((err) => {
          setError({ state: true, message: err.message });
        });
    })();
  }, [widgetRefresh]);

  if (error.state && !fetching) {
    return (
      <ErrorMessage
        message={error.message}
        retryRoute={ROUTE}
        onRetry={() => {
          setError({ state: false, message: '' });
          setFetching(true);
        }}
      />
    );
  }

  // if (true)
  if (fetching) return <WidgetLoadingAnimation widgetName={'SurfAlert'} dimensions={dimensions} />;

  if (data && Object.keys(data).length === 0) {
    return (
      <div>
        <WidgetTimeUpdate route={ROUTE} />
        <div style={{ padding: 15 }}>No data found.</div>
      </div>
    );
  }

  if (dimensions[0] == 2 && dimensions[1] == 1) {
    if (data)
      return (
        <div style={{ height: '100%' }}>
          <WidgetTimeUpdate route={ROUTE} />
          {Object.keys(data)?.length > 0 && (
            <div
              className="scrollbar-hidden"
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: widgetShellPadding,
                // height: "90%",
                // height: 400,
                height: 'calc(100% - 40px)',
                overflow: 'auto',
              }}
            >
              {/* Content */}
              <div
                style={{
                  padding: panelPadding,
                  flex: 1,
                  marginTop: 5,
                  backgroundColor: theme.primary,
                }}
              >
                {Object.keys(data).map((day, i) => {
                  const dayItem = data[day];
                  const date = new Date(day);

                  const segmentStr = dayItem['Morning']
                    ? 'Morning'
                    : dayItem['Afternoon']
                      ? 'Afternoon'
                      : dayItem['Evening']
                        ? 'Evening'
                        : 'Night';

                  return (
                    <div
                      style={{
                        marginTop: i > 0 ? dayBlockMarginTop : 0,
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          textAlign: 'center',
                          textJustify: 'middle',
                          fontWeight: 'bold',
                          fontSize: dayTitleFontSize,
                          lineHeight: 1.15,
                        }}
                      >
                        {dayItem[segmentStr].weekday + ' ' + date.toLocaleDateString()}
                      </div>
                      <div>
                        {Object.keys(dayItem).map((segment, j) => {
                          const segmentItem = data[day][segment];
                          const dateStr = DateToString(date);
                          return (
                            <ForecastSpread
                              key={i + '-' + j}
                              style={{ marginTop: j > 0 ? segmentMarginTop : 0 }}
                              header={`${segment}`}
                              headerClassName={spreadHeaderClassName}
                              headerStyle={{
                                fontWeight: 600,
                                fontSize: spreadHeaderFontSize,
                                lineHeight: 1.1,
                              }}
                              dataRowClassName={spreadDataRowClassName}
                              dataRowStyle={spreadDataRowStyle}
                              dataItemClassName={spreadDataItemClassName}
                              dataItemStyle={spreadDataItemStyle}
                              data={{
                                Wave: segmentItem.wave,
                                Alert: segmentItem.surf,
                                Wind: segmentItem.wind,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
  }

  if (dimensions[0] == 1 && dimensions[1] == 1) {
    if (data)
      return (
        <div style={{ height: '100%' }}>
          <WidgetTimeUpdate route={ROUTE} />
          {Object.keys(data)?.length > 0 && (
            <div
              className="scrollbar-hidden"
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: widgetShellPadding,
                // height: "90%",
                height: 'calc(100% - 40px)',
                overflow: 'auto',
              }}
            >
              {/* Content */}
              <div
                style={{
                  padding: panelPadding,
                  flex: 1,
                  marginTop: 5,
                  backgroundColor: theme.primary,
                }}
              >
                {Object.keys(data).map((day, i) => {
                  const dayItem = data[day];
                  const date = new Date(day);

                  const segmentStr = dayItem['Morning']
                    ? 'Morning'
                    : dayItem['Afternoon']
                      ? 'Afternoon'
                      : dayItem['Evening']
                        ? 'Evening'
                        : 'Night';

                  return (
                    <div
                      key={i}
                      style={{
                        marginTop: i > 0 ? dayBlockMarginTop : 0,
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          textAlign: 'center',
                          textJustify: 'middle',
                          fontWeight: 'bold',
                          fontSize: dayTitleFontSize,
                          lineHeight: 1.15,
                        }}
                      >
                        {dayItem[segmentStr].weekday + ' ' + date.toLocaleDateString()}
                      </div>
                      <div>
                        {Object.keys(dayItem).map((segment, j) => {
                          const segmentItem = data[day][segment];
                          const dateStr = DateToString(date);
                          return (
                            <ForecastSpread
                              key={i + '-' + j}
                              style={{ marginTop: j > 0 ? segmentMarginTop : 0 }}
                              header={`${segment}`}
                              headerClassName={spreadHeaderClassName}
                              headerStyle={{
                                fontWeight: 600,
                                fontSize: spreadHeaderFontSize,
                                lineHeight: 1.1,
                              }}
                              dataRowClassName={spreadDataRowClassName}
                              dataRowStyle={spreadDataRowStyle}
                              dataItemClassName={spreadDataItemClassName}
                              dataItemStyle={spreadDataItemStyle}
                              data={{
                                Wave: segmentItem.wave,
                                Alert: segmentItem.surf,
                                Wind: segmentItem.wind,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
  }

  if (dimensions[0] == 1 && dimensions[1] == 2) {
    if (data)
      return (
        <div style={{ height: '100%' }}>
          <WidgetTimeUpdate route={ROUTE} />
          {Object.keys(data)?.length > 0 && (
            <div
              className="scrollbar-hidden"
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: widgetShellPadding,
                // height: "90%",
                height: 'calc(100% - 40px)',
                overflow: 'auto',
              }}
            >
              {/* Content */}
              <div
                style={{
                  padding: panelPadding,
                  flex: 1,
                  marginTop: 5,
                  backgroundColor: theme.primary,
                }}
              >
                {Object.keys(data).map((day, i) => {
                  const dayItem = data[day];
                  const date = new Date(day);

                  const segmentStr = dayItem['Morning']
                    ? 'Morning'
                    : dayItem['Afternoon']
                      ? 'Afternoon'
                      : dayItem['Evening']
                        ? 'Evening'
                        : 'Night';

                  return (
                    <div
                      key={i}
                      style={{
                        marginTop: i > 0 ? dayBlockMarginTop : 0,
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          textAlign: 'center',
                          textJustify: 'middle',
                          fontWeight: 'bold',
                          fontSize: dayTitleFontSize,
                          lineHeight: 1.15,
                        }}
                      >
                        {dayItem[segmentStr].weekday + ' ' + date.toLocaleDateString()}
                      </div>
                      <div>
                        {Object.keys(dayItem).map((segment, j) => {
                          const segmentItem = data[day][segment];
                          const dateStr = DateToString(date);
                          return (
                            <ForecastSpread
                              key={i + '-' + j}
                              style={{ marginTop: j > 0 ? segmentMarginTop : 0 }}
                              header={`${segment}`}
                              headerClassName={spreadHeaderClassName}
                              headerStyle={{
                                fontWeight: 600,
                                fontSize: spreadHeaderFontSize,
                                lineHeight: 1.1,
                              }}
                              dataRowClassName={spreadDataRowClassName}
                              dataRowStyle={spreadDataRowStyle}
                              dataItemClassName={spreadDataItemClassName}
                              dataItemStyle={spreadDataItemStyle}
                              data={{
                                Wave: segmentItem.wave,
                                Alert: segmentItem.surf,
                                Wind: segmentItem.wind,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
  }
}
